import { useEffect, useState } from "react";
import {
  NewsDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { NewsService } from "../../services/news.service";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";

const News = () => {
  const [news, setNews] = useState<NewsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<NewsDataType | null>(null);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [innerImageFileList, setInnerImageFileList] = useState<any[]>([]);

  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  useEffect(() => {
    setLoading(true);
    NewsService.getAllNews()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: NewsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setNews(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await NewsService.getAllNews();
    setNews(
      updatedData?.data?.data?.map((item: NewsDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        name: "",
        description: "",
        is_visible: false,
        cover_image: null,
        internal_image: "",
        translations: [] as {
          lang_code: string;
          name: string;
          description: string;
        }[],
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Xəbərin başlığı tələb olunur"),
        description: Yup.string().required("Xəbərin təsviri tələb olunur"),
        cover_image: Yup.string().required("Xəbərin şəkili tələb olunur"),
        internal_image: Yup.string().required("Xəbərin şəkili tələb olunur"),
      }),
      onSubmit: async (values) => {
        const azTranslation = {
          lang_code: "AZ",
          name: values.name,
          description: values.description,
        };

        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];

        const finalValues = {
          ...values,
          translations: updatedTranslations,
        };
        if (isEditing && editingRecord) {
          const isEqual =
            editingRecord.name === values.name &&
            editingRecord.description === values.description &&
            editingRecord.cover_image === values.cover_image &&
            editingRecord.translations === values.translations &&
            editingRecord.internal_image === values.internal_image &&
            editingRecord.is_visible === values.is_visible;
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            if (editingRecord.cover_image === values.cover_image) {
              const payload = {
                name: values.name,
                description: values.description,
                is_visible: values.is_visible,
                cover_image:values.cover_image,
                internal_image:values.internal_image,
                translations: updatedTranslations,
              };

              if (editingRecord.cover_image !== values.cover_image) {
                payload.cover_image = values.cover_image;
              }

              if (editingRecord.image !== values.internal_image) {
                payload.internal_image = values.internal_image;
              }
              const response = await NewsService.updateNews({
                ...payload,
                id: editingRecord.id,
              });
              if (response.status === 200) {
                message.success("Partnyor uğurla yeniləndi!");
                await handleAfterSubmit();
              } else {
                message.error(
                  response.errorMessage || "Partnyoru yeniləmək alınmadı."
                );
              }
            } else {
              const response = await NewsService.updateNews({
                ...finalValues,
                id: editingRecord.id,
              });
              if (response.status === 200) {
                message.success("Partnyor uğurla yeniləndi!");
                await handleAfterSubmit();
              } else {
                message.error(
                  response.errorMessage || "Partnyoru yeniləmək alınmadı."
                );
              }
            }
          }

          
        } else {
          const response = await NewsService.addNews(finalValues);
          if (response.status === 201) {
            message.success("Xəbər uğurla əlavə edildi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Xəbər əlavə etmək alınmadı."
            );
          }
        }
        setLoading(false);
      },
    });
  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "name") {
        setFieldValue("name", value);
      } else if (field === "description") {
        setFieldValue("description", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          name: field === "name" ? value : "",
          description: field === "description" ? value : "",
        });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };
  const handleDeleteClick = async (record: NewsDataType) => {
    try {
      await NewsService.deleteNews(record.id.toString());
      setNews((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Xəbər uğurla silindi!");
    } catch (error) {
      message.error("Xəbəri silmək alınmadı.");
    }
  };

  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    NewsService.updateNews({
      id: recordId,
      is_visible: checked,
    });
    if (checked) {
      message.success("Xəbər artıq saytda görünür.");
    } else {
      message.info("Xəbər artıq saytda görünmür.");
    }
    setNews((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, is_visible: checked } : item
      )
    );
  };

  const columns: TableProps<NewsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Xəbərin adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Xəbər haqqında",
      dataIndex: "description",
      render: (_, record) => (
        <div>
          {record.description?.length > 20
            ? record.description.substring(0, 20) + "..."
            : record.description}
        </div>
      ),
    },

    {
      title: "Saytda görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image);
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${record.name}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Daxili şəkil",
      dataIndex: "internal_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.internal_image);
            setShowImageModal(true);
          }}
        >
          {record.internal_image ? (
            <img
              style={{ width: "40px" }}
              src={record.internal_image}
              alt={`${record.name}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu xəbəri siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setFieldValue("cover_image", null);
    setLogoFileList([]);
    setInnerImageFileList([]);
  };
  const handleEditClick = (record: NewsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("description", record.description);
    setFieldValue("cover_image", record.cover_image);
    setFieldValue("is_visible", record.is_visible);
    setFieldValue("translations", record.translations);
    setFieldValue("internal_image", record.internal_image);
    setLangCode("AZ");
    
    setLogoFileList(
      record.cover_image
        ? [
          {
            uid: "-1",
            name: `${record.name} image`,
            status: "done",
            url: record.cover_image,
          },
        ]
        : []
    );

    setInnerImageFileList(
      record.internal_image
        ? [
          {
            uid: "-1",
            name: `${record.name}`,
            status: "done",
            url: record.internal_image,
          },
        ]
        : []
    );
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Xəbərlər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Xəbərlər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<NewsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={news}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Xəbəri redaktə edin" : "Yeni xəbər əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Xəbərin başlığı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Xəbərin başlığı"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                      ?.name || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "name")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="Description">
              Xəbərin təsviri {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="description"
                id="description"
                placeholder="Xəbərin təsviri"
                value={
                  langCode === "AZ"
                    ? values.description
                    : values.translations.find((t) => t.lang_code === langCode)
                      ?.description || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.description}
              touched={touched.description}
            />
          </div>
          <div className="form_input">
            <div>
              <SelectImage
                image_name="Şəkil seçin"
                logoFileList={logoFileList}
                setLogoFileList={setLogoFileList}
                field="cover_image"
                setFieldValue={setFieldValue}
              />
              <ErrorMessage
                error={errors.cover_image}
                touched={touched.cover_image}
              />
            </div>
            <div>
              <SelectImage
                image_name="Daxili şəkili seçin"
                logoFileList={innerImageFileList}
                setLogoFileList={setInnerImageFileList}

                field="internal_image"
                setFieldValue={setFieldValue}
              />
              <ErrorMessage
                error={errors.internal_image}
                touched={touched.internal_image}
              />
            </div>
          </div>

          <div className="form_input">
            <label className="active_label" htmlFor="">
              Xeberin saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default News;
